import { applicationSequenceIdentifier } from '@shared/applicationIdentity';
import type { StatusEvidence } from '@/hooks/useStatusEvidence';
import { unlinkedCompanyProfileNotice } from '@/lib/companyProfileNotice';
import type { ReactNode, ReactElement } from 'react';
import { REGION_FLAGS, type Region } from '@/constants/regions';
import { StatusBadge } from './StatusBadge';
import { getStatusConfig, normalizeStatus } from '@/constants/status';
import type { CompanyProfileLinkStatus } from '@/api/v2Client';

/**
 * Shape SubmissionHeroCard renders. Compatible with the V2 `Submission`
 * type but defined locally so Wave-C consumers can pass enriched or
 * synthetic submission objects without coupling this card to v2Client.
 *
 * Optional fields (Applicant / Indication / etc.) are
 * not yet on the V2 Submission shape; they are accepted here so Wave C
 * can wire them in once the backend exposes them.
 */
export interface SubmissionLike {
  readonly Id?: string;
  readonly DrugName: string;
  readonly Region: Region;
  readonly Status?: string | null;
  readonly SequenceNumber?: string | null;
  readonly NdaNumber?: string | null;
  readonly DossierId?: string | null;
  readonly EuApplicationUuid?: string | null;
  readonly SubmissionType?: string | null;
  readonly FdaActivityMetadataNeedsReview?: boolean;
  readonly Applicant?: string | null;
  readonly Indication?: string | null;
  readonly UpdatedAt?: string | null;
  /** phase-fix-submission-details-polish (review §2 #16) — target eCTD version. */
  readonly EctdVersion?: string | null;
  /** phase-fix-submission-details-polish (review §2 #14) — archived = hidden. */
  readonly IsArchived?: boolean;
  /** phase-submission-spec-pinning — the regional specification this
   * application files under (set at creation, kept for the lifecycle). */
  readonly SpecFileTitle?: string | null;
  readonly SpecFileVersion?: string | null;
  /** T15-D3 — server wording when NO specification is pinned (null when pinned). */
  readonly SpecPinWarning?: string | null;
  /** phase-missing-profile-visibility — see v2Client.CompanyProfileLinkStatus. */
  readonly CompanyProfileLinkStatus?: CompanyProfileLinkStatus;
}

export interface SubmissionHeroCardProps {
  readonly submission: SubmissionLike;
  /** Optional doc-count summary rendered as "x of n confirmed". */
  readonly confirmedCount?: number;
  readonly totalDocs?: number;
  /** Optional override for the rendered submission-type label.
   * V2 stores SubmissionType as a free-form key (e.g. "nda-original-505b1");
   * callers that know the human label can pass it explicitly. */
  readonly submissionTypeLabel?: string;
  /**
   * Phase phase-ui-consistency-sweep (§3B) — action buttons (Edit, gear,
   * …) rendered in a consistent slot at the right of the navy header band.
   * Previously the Submission Detail page floated these absolutely on top
   * of the coloured bar; routing them through this slot keeps them placed
   * consistently across screens.
   */
  readonly headerActions?: ReactNode;
  /**
   * phase-missing-profile-visibility — clicking the missing/never-linked
   * profile badge opens the caller's existing relink UI (the flag never
   * builds its own relink mechanism; see item 4 of that phase). Omit to
   * render the badge without a click affordance.
   */
  readonly onRelinkProfileClick?: () => void;
  /** Slot for screen-specific banners (workflow pills, ESG, progress). */
  readonly children?: ReactNode;
  readonly hideHeader?: boolean;
}

/**
 * Phase phase-ui-consistency-sweep (§3B) — the card's document summary
 * must reflect the REAL document count, not a status-derived guess. The
 * old behaviour rendered the Draft status text "No documents uploaded
 * yet" purely from status, so a submission that already had documents
 * (e.g. "2 documents passed validation") still falsely claimed none.
 *
 * Now: when the count is known we show it; when it is genuinely zero we
 * keep the "no documents" message; when the count is unknown we show a
 * neutral Draft label that never falsely claims zero.
 */
function heroStatusText(
  headerStatusText: string,
  normalized: string,
  totalDocs: number | undefined,
): string {
  if (normalized !== 'Draft') return headerStatusText;
  if (typeof totalDocs === 'number') {
    return totalDocs === 0
      ? 'No documents uploaded yet'
      : `${totalDocs} document${totalDocs === 1 ? '' : 's'} uploaded`;
  }
  return 'Draft';
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString();
}

export function SubmissionHeroCard({
  submission,
  confirmedCount,
  totalDocs,
  submissionTypeLabel,
  headerActions,
  onRelinkProfileClick,
  children,
  hideHeader = false,
}: SubmissionHeroCardProps): ReactElement {
  const normalized = normalizeStatus(submission.Status);
  const stCfg = getStatusConfig(submission.Status);
  const typeText = submissionTypeLabel ?? submission.SubmissionType ?? '';
  const seq = submission.SequenceNumber ?? '0000';
  const lastDate = formatDate(submission.UpdatedAt);
  const isHidden = submission.IsArchived === true;

  return (
    <div
      data-hidden={isHidden ? 'true' : 'false'}
      style={{
        marginBottom: 20,
        border: '1px solid var(--border)',
        borderRadius: 10,
        overflow: 'hidden',
        boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
        // Same de-emphasis the dashboard gives hidden rows (SubmissionSubRow).
        ...(isHidden ? { opacity: 0.55 } : {}),
      }}
    >
      {!hideHeader && <SubmissionIdentityHeader submission={submission} headerActions={headerActions} onRelinkProfileClick={onRelinkProfileClick} />}

      {/* Card body */}
      <div style={{ background: 'var(--surface)', padding: '14px 20px' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: 16,
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {submission.Applicant && (
              <div style={{ fontSize: 13, color: 'var(--text)' }}>{submission.Applicant}</div>
            )}
            {submission.Indication && (
              <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>{submission.Indication}</div>
            )}
            {typeText && (
              <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{typeText}</div>
            )}
          </div>
          <div
            style={{
              textAlign: 'right',
              display: 'flex',
              flexDirection: 'column',
              gap: 3,
            }}
          >
            <div
              data-testid="hero-seq-line"
              style={{
                fontFamily: 'monospace',
                fontSize: 13,
                fontWeight: 600,
                // phase-ui-hero-right-metadata-contrast — the block's primary
                // line reads at full text contrast (it sits on the WHITE card
                // body — verified live on the dev site — so light-on-navy
                // styling would be wrong here; the muted secondary lines pass
                // WCAG AA on white).
                color: 'var(--text)',
              }}
            >
              {`Seq ${seq}${normalized === 'Complete' ? ' 🔒' : ''}`}
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
              {heroStatusText(stCfg.headerStatusText, normalized, totalDocs)}
            </div>
            {typeof confirmedCount === 'number' && typeof totalDocs === 'number' && totalDocs > 0 && (
              <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                {`${confirmedCount} of ${totalDocs} confirmed`}
              </div>
            )}
            {/* T-17 (T17-D4) — the "Validated: <status>" line is gone. It read
                `LastValidatedAt` (a column that does not exist) and the unmaintained
                `ValidationStatus`, so it never rendered truthfully. Validation state
                is shown where it is derived from persisted results: the Status
                pipeline's Validated stage and the Compiler's validation badge. */}
            {lastDate && (
              <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{lastDate}</div>
            )}
          </div>
        </div>
        {children}
      </div>
    </div>
  );
}

export function SubmissionIdentityHeader({ submission, statusEvidence, headerActions, onRelinkProfileClick, submissionTypeLabel, showSequence = false }: Pick<SubmissionHeroCardProps, 'submission' | 'headerActions' | 'onRelinkProfileClick' | 'submissionTypeLabel'> & { showSequence?: boolean; statusEvidence?: StatusEvidence }): ReactElement {
  const applicationId = applicationSequenceIdentifier(submission.Region, { ...submission })?.value;
  const flag = REGION_FLAGS[submission.Region] ?? '';
  const isHidden = submission.IsArchived === true;
  const ectdVersion = submission.EctdVersion?.trim() || null;
  const v4NotReal = ectdVersion !== null && ectdVersion.startsWith('4');
  return (
<div
        data-testid="submission-identity-header"
        style={{
          background: 'var(--primary)',
          padding: '14px 20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: showSequence ? 'flex-start' : 'space-between',
          flexWrap: 'wrap',
          gap: showSequence ? 8 : 12,
        }}
      >
        <div style={{ display: showSequence ? 'contents' : 'flex', alignItems: 'baseline', flexWrap: 'wrap', gap: 10 }}>
          <span style={{ fontSize: 20, fontWeight: 700, color: '#fff', maxWidth: '100%', overflowWrap: 'anywhere' }}>
            {submission.DrugName}
          </span>
          {showSequence && <span style={{ color: '#fff', fontSize: 13, whiteSpace: 'nowrap' }}>Seq {submission.SequenceNumber ?? '—'}</span>}
          {showSequence && <span style={{ color: 'rgba(255,255,255,0.85)', fontSize: 13, overflowWrap: 'anywhere', maxWidth: '100%' }}>Application: {applicationId ?? '—'}</span>}
          {!showSequence && applicationId && (
            <span
              style={{
                fontFamily: 'monospace',
                fontSize: 14,
                color: 'rgba(255,255,255,0.7)',
              }}
            >
              {applicationId}
            </span>
          )}
        </div>
        <div style={{ display: showSequence ? 'contents' : 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
          <span style={{ fontSize: 16 }} aria-hidden="true">
            {flag}
          </span>
          <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.8)' }}>
            {submission.Region}
          </span>
          {showSequence && <span data-testid="hero-ectd-version" style={{ color: '#fff', background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.3)', borderRadius: 6, padding: '3px 8px', fontSize: 12, whiteSpace: 'nowrap' }}>eCTD {ectdVersion ? `v${ectdVersion}` : '—'}</span>}
          {showSequence ? (
            <details className="submission-more-details">
              <summary>{v4NotReal || submission.SpecPinWarning ? "⚠ More details" : "More details"}</summary>
              <SubmissionIdentityWarnings submission={submission} onRelinkProfileClick={onRelinkProfileClick} />
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, paddingTop: 12 }}>
                {submission.Id && <span data-testid="submission-detail-id" style={{ overflowWrap: 'anywhere' }}>Submission ID: <span style={{ fontFamily: 'monospace' }}>{submission.Id}</span></span>}
                <span>Company: {submission.Applicant || '—'}</span>
                <span>Submission type: {submissionTypeLabel || submission.SubmissionType || '—'}</span>
                {submission.Indication && <span>Indication: {submission.Indication}</span>}
                {submission.UpdatedAt && <span>Updated: {formatDate(submission.UpdatedAt)}</span>}
                {!submission.SpecFileTitle && <span>Specification: not pinned</span>}
                          {/* phase-submission-spec-pinning — what this application files
              under. The pin belongs to the submission (set at creation,
              carried through every sequence), not to the region's current
              active flag. Read-only here; changing it is an audited
              administrative action. */}
          {typeof submission.SpecFileTitle === 'string' && submission.SpecFileTitle !== '' && (
            <span
              data-testid="hero-spec-pin"
              title="The regional specification this application files under — set when the application was created and kept for its whole lifecycle. Every sequence of this application files under the same specification. Changing it is an exceptional, audited administrative action."
              style={{
                fontFamily: 'monospace',
                fontSize: 11,
                fontWeight: 600,
                padding: '2px 8px',
                borderRadius: 8,
                whiteSpace: 'nowrap',
                background: 'rgba(255,255,255,0.14)',
                border: '1px solid rgba(255,255,255,0.25)',
                color: 'rgba(255,255,255,0.85)',
              }}
            >
              {`Files under: ${submission.SpecFileTitle}${
                typeof submission.SpecFileVersion === 'string' &&
                submission.SpecFileVersion !== '' &&
                !submission.SpecFileTitle.includes(submission.SpecFileVersion)
                  ? ` v${submission.SpecFileVersion}`
                  : ''
              }`}
            </span>
          )}

              </div>
            </details>
          ) : (<>
          {ectdVersion !== null && (
            <span
              data-testid="hero-ectd-version"
              title={
                v4NotReal
                  ? 'This sequence targets eCTD v4.0. Package generation for v4.0 is not yet supported — compilation is blocked until a v4.0 generator is available.'
                  : `This sequence targets eCTD v${ectdVersion} per the region's policy.`
              }
              style={{
                fontFamily: 'monospace',
                fontSize: 11,
                fontWeight: 600,
                padding: '2px 8px',
                borderRadius: 8,
                whiteSpace: 'nowrap',
                background: v4NotReal ? 'rgba(245,158,11,0.25)' : 'rgba(255,255,255,0.14)',
                border: v4NotReal ? '1px solid rgba(245,158,11,0.9)' : '1px solid rgba(255,255,255,0.25)',
                color: v4NotReal ? '#FDE68A' : 'rgba(255,255,255,0.85)',
              }}
            >
              {v4NotReal ? `eCTD v${ectdVersion} — generation not yet supported` : `eCTD v${ectdVersion}`}
            </span>
          )}
          {/* phase-submission-spec-pinning — what this application files
              under. The pin belongs to the submission (set at creation,
              carried through every sequence), not to the region's current
              active flag. Read-only here; changing it is an audited
              administrative action. */}
          {typeof submission.SpecFileTitle === 'string' && submission.SpecFileTitle !== '' && (
            <span
              data-testid="hero-spec-pin"
              title="The regional specification this application files under — set when the application was created and kept for its whole lifecycle. Every sequence of this application files under the same specification. Changing it is an exceptional, audited administrative action."
              style={{
                fontFamily: 'monospace',
                fontSize: 11,
                fontWeight: 600,
                padding: '2px 8px',
                borderRadius: 8,
                whiteSpace: 'nowrap',
                background: 'rgba(255,255,255,0.14)',
                border: '1px solid rgba(255,255,255,0.25)',
                color: 'rgba(255,255,255,0.85)',
              }}
            >
              {`Files under: ${submission.SpecFileTitle}${
                typeof submission.SpecFileVersion === 'string' &&
                submission.SpecFileVersion !== '' &&
                !submission.SpecFileTitle.includes(submission.SpecFileVersion)
                  ? ` v${submission.SpecFileVersion}`
                  : ''
              }`}
            </span>
          )}
          </>)}
          {!showSequence && <SubmissionIdentityWarnings submission={submission} onRelinkProfileClick={onRelinkProfileClick} includeFormatWarning={false} />}
          {isHidden && (
            <span
              data-testid="hero-hidden-badge"
              title="This sequence is hidden from the default dashboard view (archived). It remains fully functional."
              style={{
                display: 'inline-block',
                padding: '2px 7px',
                borderRadius: 8,
                fontSize: 10,
                fontWeight: 600,
                background: 'rgba(255,255,255,0.16)',
                border: '1px solid rgba(255,255,255,0.35)',
                color: 'rgba(255,255,255,0.85)',
                whiteSpace: 'nowrap',
              }}
            >
              Hidden
            </span>
          )}
          <StatusBadge status={submission.Status} evidence={statusEvidence} />
          {headerActions && (
            <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginLeft: showSequence ? 'auto' : undefined }}>{headerActions}</div>
          )}
        </div>
        {submission.Region === 'FDA' && submission.FdaActivityMetadataNeedsReview === true && (
          <div role="note" data-testid="fda-activity-review-notice" style={{ flexBasis: '100%', padding: '10px 14px', background: '#fffbeb', border: '1px solid #f5d89a', borderRadius: 6, color: '#713f12', fontSize: 13 }}>
            <strong>FDA activity metadata needs review.</strong> Earlier activity labels did not match the FDA code list. Saved codes have been preserved. Before compiling, an authorized editor must review and reselect the activity and subtype on Submission Details using the corrected choices.
          </div>
        )}
      </div>
  );
}

/** Warnings remain outside the optional information disclosure. */
export function SubmissionIdentityWarnings({ submission, onRelinkProfileClick, includeFormatWarning = true }: Pick<SubmissionHeroCardProps, 'submission' | 'onRelinkProfileClick'> & { includeFormatWarning?: boolean }): ReactElement | null {
  const version = submission.EctdVersion?.trim();
  const unsupported = includeFormatWarning && version?.startsWith('4');
  const missingPin = !submission.SpecFileTitle && Boolean(submission.SpecPinWarning);
  const profileWarning = submission.CompanyProfileLinkStatus?.state === 'missing' || submission.CompanyProfileLinkStatus?.state === 'unlinked';
  if (!unsupported && !missingPin && !profileWarning) return null;
  return <aside aria-label="Submission notices" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 6, padding: '10px 16px', background: '#fffbeb', borderTop: '1px solid #f5d89a', color: '#713f12', fontSize: 12 }}>
    {unsupported && <div role="note"><strong>eCTD v{version} — generation not yet supported.</strong> Compilation is blocked until a v4.0 generator is available.</div>}
          {/* T15-D3 — no pin is stated, never left blank. */}
          {(typeof submission.SpecFileTitle !== 'string' || submission.SpecFileTitle === '') &&
            typeof submission.SpecPinWarning === 'string' &&
            submission.SpecPinWarning !== '' && (
              <span
                data-testid="hero-spec-pin-missing"
                role="note"
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  padding: '2px 8px',
                  borderRadius: 8,
                  background: 'rgba(251,191,36,0.2)',
                  border: '1px solid rgba(251,191,36,0.6)',
                  color: '#713f12',
                }}
              >
                {submission.SpecPinWarning}
              </span>
            )}
          {/* phase-missing-profile-visibility — Sharon's ruling, 2026-08-03:
              the wrong-company hazard must be visible the moment it exists,
              not only when generation refuses. Two mutually-exclusive
              variants of the same underlying fact (CompanyProfileLinkStatus,
              computed by the SAME evaluator generation itself uses). Both
              are clickable when the caller wires onRelinkProfileClick, which
              opens the caller's EXISTING edit/relink UI — this badge never
              builds a parallel relink mechanism. */}
          {submission.CompanyProfileLinkStatus?.state === 'missing' && (
            <button
              type="button"
              data-testid="hero-profile-missing"
              onClick={onRelinkProfileClick}
              disabled={!onRelinkProfileClick}
              title="Company profile missing; document generation is unavailable until it is restored or relinked. Click to relink."
              style={{
                fontFamily: 'inherit',
                fontSize: 12,
                fontWeight: 700,
                padding: '2px 8px',
                borderRadius: 8,
                whiteSpace: 'normal',
                background: 'rgba(220,38,38,0.32)',
                border: '1px solid rgba(248,113,113,0.9)',
                color: '#991b1b',
                cursor: onRelinkProfileClick ? 'pointer' : 'default',
              }}
            >
              ⚠ Company profile missing
            </button>
          )}
          {submission.CompanyProfileLinkStatus?.state === 'unlinked' &&
            (() => {
              const st = submission.CompanyProfileLinkStatus;
              if (st.state !== 'unlinked') return null;
              // ISS-020 — generation REFUSES with no linked profile (D-074:
              // NoLinkedCompanyProfileError, 409). The old copy promised the
              // tenant default would be substituted, contradicting the gate;
              // the flag now names the explicit-link requirement instead.
              const arbitrary = st.profileCount > 1;
              const label = 'No company profile linked';
              const title = unlinkedCompanyProfileNotice(st.profileCount);
              return (
                <button
                  type="button"
                  data-testid="hero-profile-unlinked"
                  onClick={onRelinkProfileClick}
                  disabled={!onRelinkProfileClick}
                  title={title}
                  style={{
                    fontFamily: 'inherit',
                    fontSize: 12,
                    fontWeight: 700,
                    padding: '2px 8px',
                    borderRadius: 8,
                    whiteSpace: 'normal',
                    background: arbitrary ? 'rgba(245,158,11,0.32)' : 'rgba(245,158,11,0.18)',
                    border: '1px solid rgba(245,158,11,0.9)',
                    color: '#713f12',
                    cursor: onRelinkProfileClick ? 'pointer' : 'default',
                  }}
                >
                  {arbitrary ? '⚠ ' : ''}
                  {label}
                </button>
              );
            })()}

  </aside>;
}
